import { toast } from "sonner";
import * as React from "react";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Order } from "../../../helpers/TableHelpers";
import {
  MigrationStatus,
  MigrationStatusColors,
  MigrationStatusIcons,
  MigrationStatusMigrationEntityLabels,
} from "../../../models/MigrationStatus";
import { FlagType } from "../../../models/ClientConfiguration/FlaggingConfiguration";
import { FilterCombobox } from "../FilterCombobox";
import MigrationStatusChip from "../MigrationStatusChip";
import SortableTableColumnHeader from "../SortableTableColumnHeader";
import { MigrationFlagStatus, MigrationFlagStatusSelectOptions } from "../../../models/Flags/MigrationFlagStatus";
import { colorVariantClasses } from "@/theme/AppColorVariant";
import FlagTypeChip from "../FlagTypeChip";
import { UserRole } from "../../../models/User";
import ConfirmMigrationEntityBlockUpdateModal from "../ConfirmMigrationEntityBlockUpdateModal";
import TextHighlighter from "../TextHighlighter";
import TableEmptyState from "../TableEmptyState";
import { cn } from "@/lib/utils";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  tableWrapperClassName,
} from "../Table/TableStyles";
import { SearchInput } from "../Table/SearchInput";
import { FlagStatusFilter, useFlagStatusFilter } from "../Table/FlagStatusFilter";
import TablePagination from "../Table/TablePagination";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Trash2, FileSpreadsheet, Lock, LockOpen, Ban, AlertTriangle, CheckCircle2, FileWarning } from "lucide-react";
import { useUserLookup } from "../../../hooks/useUserLookup";
import { deletionWouldStrandUploads, STRANDED_UPLOADS_HINT } from "../../../lib/migrationEntityDeletion";

/** Common shape every migration entity must satisfy. */
export interface MigrationEntityBase {
  id: string;
  isLocked: boolean;
  status: MigrationStatus;
  organizationId: string;
  statistics: {
    totalNumberOfFiles: number;
    filesMigrated: number;
    duplicateFilesSkipped?: number;
    filesSizeInMb: number;
    unresolvedConflictCount?: number;
  };
  flagStatus: MigrationFlagStatus | null;
  flagType: FlagType | null;
  flaggedBy: string | null;
  hasRootFolderInfo: boolean;
  hasFlaggedPaths: boolean;
}

export interface MigrationEntityTableConfig<TEntity extends MigrationEntityBase> {
  /** Display labels */
  entityName: string;
  searchLabel: string;
  searchPlaceholder: string;
  emptyMessage: string;

  /** Access the human-readable entity key (caseNumber, intakeNumber, etc.) */
  getEntityKey: (entity: TEntity) => string;

  /** Sort property for the entity key column */
  entityKeySortProperty: string;

  /** Data fetching */
  queryKey: string;
  /** Detail query key for single-entity view, used to invalidate the detail cache after mutations */
  detailQueryKey: string;
  fetchEntities: (params: {
    organizationId: string;
    search: string[] | null;
    descending: boolean;
    orderBy: string;
    status: string[] | null;
    isLocked: boolean | null;
    hasFlaggedPaths: boolean | null;
    hasConflicts: boolean | null;
    flagStatus: MigrationFlagStatus[] | null;
    pageNumber: number;
    pageSize: number;
  }) => Promise<{ data: TEntity[]; paging?: { totalCount: number } | null }>;

  /** Sort the list opens with; "status" when not given. */
  defaultSort?: string;
  /** Makes the Flag Status column sortable, by this property. */
  flagStatusSortProperty?: string;

  /** Routing */
  getDetailPath: (organizationId: string, entityId: string) => string;

  /** Active explorer enum values */
  deleteExplorerValue: number;
  exportExplorerValue: number;

  /** Delete request factory */
  createDeleteRequest: (entity: TEntity, organizationId: string) => { migrationEntityId: string };

  /** Flag update */
  updateFlag: (entityId: string, flagStatus: MigrationFlagStatus) => Promise<void>;

  /** Block update */
  updateBlock: (entityId: string, isBlocked: boolean) => Promise<void>;
  blockConfig: { migrationEntityType: string };

  /** Flag modal render prop — each entity has its own flag modal with different prop names */
  renderFlagModal: (props: {
    open: boolean;
    entityKey: string;
    flagStatus: MigrationFlagStatus;
    onClose: () => void;
    onConfirm: () => void;
  }) => React.ReactNode;
}

export interface MigrationEntityTableProps<TEntity extends MigrationEntityBase> {
  config: MigrationEntityTableConfig<TEntity>;
  organizationId: string;
  activeJobsExplorer: number;
  newForDeletion: { migrationEntityId: string }[];
  setNewForDeletion: React.Dispatch<React.SetStateAction<any[]>>;
  newForExport: TEntity[];
  setNewForExport: React.Dispatch<React.SetStateAction<TEntity[]>>;
  userRole: UserRole;
  isAssignedUser: boolean;
}

function MigrationEntityTable<TEntity extends MigrationEntityBase>({
  config,
  organizationId,
  activeJobsExplorer,
  newForDeletion,
  setNewForDeletion,
  newForExport,
  setNewForExport,
  userRole,
  isAssignedUser,
}: MigrationEntityTableProps<TEntity>) {
  const [order, setOrder] = React.useState<Order>("asc");
  const [sort, setSort] = React.useState<string>(config.defaultSort ?? "status");
  const [lockFilter, setLockFilter] = React.useState<boolean | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [flaggedPathsFilter, setFlaggedPathsFilter] = React.useState<boolean | null>(null);
  const [conflictsFilter, setConflictsFilter] = React.useState<boolean | null>(null);
  const [searchTerm, setSearchTerm] = useState<string | null>("");
  const [page, setPage] = React.useState(0);
  const [flagStatusFilter, setFlagStatusFilter] = useFlagStatusFilter(() => setPage(0));
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [entities, setEntities] = useState<TEntity[]>([]);
  const [rows, setRows] = useState(0);
  const [isFlagModalOpen, setIsFlagModalOpen] = React.useState<boolean>(false);
  const [dirtyFlagStatus, setDirtyFlagStatus] = useState<MigrationFlagStatus>();
  const [dirtyEntityId, setDirtyEntityId] = useState<string>("");
  const [dirtyEntityKey, setDirtyEntityKey] = useState<string>("");
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = React.useState<boolean>(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getUserName } = useUserLookup();

  const invalidateAfterMutation = (entityId: string) => Promise.all([
    queryClient.invalidateQueries({ queryKey: [config.queryKey] }),
    queryClient.invalidateQueries({ queryKey: [config.detailQueryKey, entityId] }),
  ]);

  const entitiesQuery = useQuery({
    queryKey: [
      config.queryKey, organizationId, searchTerm, page, rowsPerPage,
      statusFilter, lockFilter, flaggedPathsFilter, conflictsFilter, flagStatusFilter, sort, order,
    ],
    queryFn: async () => config.fetchEntities({
      organizationId,
      search: searchTerm ? [searchTerm] : null,
      descending: order === "desc",
      orderBy: sort,
      status: statusFilter ? [statusFilter] : null,
      isLocked: lockFilter,
      hasFlaggedPaths: flaggedPathsFilter,
      hasConflicts: conflictsFilter,
      flagStatus: flagStatusFilter ? [flagStatusFilter] : null,
      pageNumber: page + 1,
      pageSize: rowsPerPage,
    }),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000 + 1,
    refetchIntervalInBackground: true,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (entitiesQuery.data?.data) {
      setEntities(entitiesQuery.data.data);
      setRows(entitiesQuery.data.paging?.totalCount ?? 0);
    }
  }, [entitiesQuery]);

  const changeSort = (newSort: string) => { setPage(0); setSort(newSort); };
  const changeOrder = (newOrder: Order) => { setPage(0); setOrder(newOrder); };

  const handleDelete = (entity: TEntity) => {
    if (!disabledRows.has(entity.id)) {
      setNewForDeletion((prev: any[]) => [...prev, config.createDeleteRequest(entity, organizationId)]);
    }
  };

  const handleAddForExport = (entity: TEntity) => {
    if (!disabledRows.has(entity.id)) {
      setNewForExport((prev) => [...prev, entity]);
    }
  };

  const getDisabledRows = () => {
    switch (activeJobsExplorer) {
      case config.deleteExplorerValue:
        return new Set(newForDeletion.map((d) => d.migrationEntityId));
      case config.exportExplorerValue:
        return new Set(newForExport.map((e) => e.id));
      default:
        return new Set<string>();
    }
  };

  const getActiveButton = (entity: TEntity, isDisabled: boolean) => {
    if (userRole === UserRole.Viewer) return <></>;
    switch (activeJobsExplorer) {
      case config.deleteExplorerValue:
        return (
          <span title={deletionWouldStrandUploads(entity) ? STRANDED_UPLOADS_HINT : undefined}>
            <Button variant="ghost" size="icon-sm" aria-label="Delete"
              disabled={isDisabled || deletionWouldStrandUploads(entity)}
              className="text-destructive" onClick={() => handleDelete(entity)}>
              <Trash2 className="size-4" />
            </Button>
          </span>
        );
      case config.exportExplorerValue:
        return (
          <Button variant="ghost" size="icon-sm" aria-label="Csv"
            disabled={isDisabled} className="text-emerald-600" onClick={() => handleAddForExport(entity)}>
            <FileSpreadsheet className="size-4" />
          </Button>
        );
      default:
        return <></>;
    }
  };

  const handleFlagStatusModal = (entityKey: string, entityId: string, flagStatus: MigrationFlagStatus, isOpen: boolean) => {
    setIsFlagModalOpen(isOpen);
    setDirtyEntityKey(entityKey);
    setDirtyEntityId(entityId);
    setDirtyFlagStatus(flagStatus);
  };

  const handleBlockModal = (entityKey: string, entityId: string, blocked: boolean, isOpen: boolean) => {
    setIsBlockModalOpen(isOpen);
    setDirtyEntityKey(entityKey);
    setDirtyEntityId(entityId);
    setIsBlocked(blocked);
  };

  const { isError, isFetching } = entitiesQuery;
  const disabledRows = getDisabledRows();

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={fileFiltersBoxClassName}>
        <SearchInput label={config.searchLabel} placeholder={config.searchPlaceholder} onSearch={(value) => setSearchTerm(value ?? null)} />
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Status</Label>
          <FilterCombobox value={statusFilter}
            onChange={(v) => { setPage(0); setStatusFilter(v); }}
            placeholder="Status"
            options={Object.values(MigrationStatus)
              .filter((v) => v !== MigrationStatus.NotFound && v !== MigrationStatus.InterruptedDelta && v !== MigrationStatus.NotFoundMarkedForRetry && v !== MigrationStatus.SkippedDuplicate)
              .map((s) => ({ value: s as string, label: MigrationStatusMigrationEntityLabels[s as MigrationStatus], color: MigrationStatusColors[s as MigrationStatus], icon: MigrationStatusIcons[s as MigrationStatus] }))}
          />
        </div>
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Lock</Label>
          <FilterCombobox
            value={lockFilter === null ? null : lockFilter ? "locked" : "unlocked"}
            onChange={(v) => { setPage(0); setLockFilter(v === null || v === "all" ? null : v === "locked"); }}
            placeholder="Any" options={[{ value: "locked", label: "Locked", icon: Lock }, { value: "unlocked", label: "Unlocked", icon: LockOpen }]}
          />
        </div>
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Flagged paths</Label>
          <FilterCombobox
            value={flaggedPathsFilter === null ? null : flaggedPathsFilter ? "has-flagged-paths" : "no-flagged-paths"}
            onChange={(v) => { setPage(0); setFlaggedPathsFilter(v === null || v === "all" ? null : v === "has-flagged-paths"); }}
            placeholder="Any" options={[{ value: "has-flagged-paths", label: "Has flagged paths", icon: AlertTriangle }, { value: "no-flagged-paths", label: "No flagged paths", icon: CheckCircle2 }]}
          />
        </div>
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Conflicts</Label>
          <FilterCombobox
            value={conflictsFilter === null ? null : conflictsFilter ? "has-conflicts" : "no-conflicts"}
            onChange={(v) => { setPage(0); setConflictsFilter(v === null || v === "all" ? null : v === "has-conflicts"); }}
            placeholder="Any" options={[{ value: "has-conflicts", label: "Has conflicts", icon: FileWarning }, { value: "no-conflicts", label: "No conflicts", icon: CheckCircle2 }]}
          />
        </div>
        <FlagStatusFilter value={flagStatusFilter} onChange={(v) => { setPage(0); setFlagStatusFilter(v); }} />
      </div>

      <div className={cn(tableWrapperClassName, (isFetching || isError) && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[20%]"><SortableTableColumnHeader label="Number" sortProperty={config.entityKeySortProperty} getSort={() => sort} setSort={changeSort} getOrder={() => order} setOrder={changeOrder} /></TableHead>
              <TableHead className="w-[5%]">Lock</TableHead>
              <TableHead className="w-[11%]"><SortableTableColumnHeader label="Status" sortProperty="status" getSort={() => sort} setSort={changeSort} getOrder={() => order} setOrder={changeOrder} /></TableHead>
              <TableHead className="w-[7%]">Progress</TableHead>
              <TableHead className="w-[8%]"><SortableTableColumnHeader label="Files" sortProperty="totalNumberOfFiles" getSort={() => sort} setSort={changeSort} getOrder={() => order} setOrder={changeOrder} /></TableHead>
              <TableHead className="w-[9%]"><SortableTableColumnHeader label="Size" sortProperty="totalFilesSize" getSort={() => sort} setSort={changeSort} getOrder={() => order} setOrder={changeOrder} /></TableHead>
              <TableHead className="w-[10%]">Flag Type</TableHead>
              <TableHead className="w-[10%]">Blocked By</TableHead>
              <TableHead className="w-[12%]">
                {config.flagStatusSortProperty
                  ? <SortableTableColumnHeader label="Flag Status" sortProperty={config.flagStatusSortProperty} getSort={() => sort} setSort={changeSort} getOrder={() => order} setOrder={changeOrder} />
                  : "Flag Status"}
              </TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>

          <TableBody>
            {entities.map((entity) => {
              const migratedPct = entity.statistics.totalNumberOfFiles > 0
                ? ((entity.statistics.filesMigrated + (entity.statistics.duplicateFilesSkipped ?? 0)) / entity.statistics.totalNumberOfFiles) * 100
                : -1;
              const flagOption = MigrationFlagStatusSelectOptions.find((o) => o.id === entity.flagStatus) || MigrationFlagStatusSelectOptions[0];
              const isDisabled = disabledRows.has(entity.id);
              const entityKey = config.getEntityKey(entity);

              return (
                <TableRow key={entity.id}
                  className={cn("cursor-pointer", isDisabled && "opacity-50 pointer-events-none")}
                  tabIndex={0}
                  onClick={() => navigate(config.getDetailPath(organizationId, entity.id))}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(config.getDetailPath(organizationId, entity.id)); } }}>
                  <TableCell className="whitespace-normal break-all">
                    <div className="flex items-center gap-1.5">
                      <TextHighlighter text={entityKey} searchTerm={searchTerm} level="body-sm" />
                      {(entity.statistics.unresolvedConflictCount ?? 0) > 0 && (
                        <TooltipProvider><Tooltip><TooltipTrigger asChild>
                          <FileWarning className="size-4 shrink-0 text-destructive" aria-label="Has unresolved conflicts" />
                        </TooltipTrigger><TooltipContent>Has unresolved conflicts</TooltipContent></Tooltip></TooltipProvider>
                      )}
                      {/* Warning rather than destructive, and a different glyph: a flagged path is a decision
                          nobody has taken yet, not something broken. AlertTriangle matches the flagged-paths
                          filter above, so the indicator and the filter that finds it agree. */}
                      {entity.hasFlaggedPaths && (
                        <TooltipProvider><Tooltip><TooltipTrigger asChild>
                          <AlertTriangle className={cn("size-4 shrink-0", colorVariantClasses.warning.text)} aria-label="Has flagged paths" />
                        </TooltipTrigger><TooltipContent>Has flagged paths</TooltipContent></Tooltip></TooltipProvider>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{entity.isLocked ? <Badge variant="primary"><Lock className="size-3" />Locked</Badge> : <></>}</TableCell>
                  <TableCell><MigrationStatusChip status={entity.status} /></TableCell>
                  <TableCell>
                    {entity.statistics.totalNumberOfFiles > 0 ? (
                      <Badge variant={migratedPct > 99.99 ? "success" : "primary"}>
                        {migratedPct % 1 > 0.99 || migratedPct % 1 < 0.01 ? `${migratedPct.toFixed(0)}%` : `${migratedPct.toFixed(2)}%`}
                      </Badge>
                    ) : <Badge variant="secondary">No files</Badge>}
                  </TableCell>
                  <TableCell><Badge variant="neutral">{String(entity.statistics.totalNumberOfFiles ?? 0)}</Badge></TableCell>
                  <TableCell><Badge variant="neutral">{`${entity.statistics.filesSizeInMb.toFixed(2)} MB`}</Badge></TableCell>
                  <TableCell>{entity.flagType ? <FlagTypeChip flagType={entity.flagType} /> : <></>}</TableCell>
                  <TableCell><span className="text-sm whitespace-normal break-words">{getUserName(entity.flaggedBy)}</span></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {entity.flagType && entity.flagType === FlagType.MigrationEntityWithMoreThan10KFiles ? (
                      <Select value={flagOption.id} onValueChange={(v) => handleFlagStatusModal(entityKey, entity.id, v as MigrationFlagStatus, true)}
                        disabled={isDisabled || userRole === UserRole.Viewer || (!isAssignedUser && userRole !== UserRole.Admin)}>
                        <SelectTrigger className={cn("w-full max-w-[150px]", flagOption.color && colorVariantClasses[flagOption.color]?.text)}>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {MigrationFlagStatusSelectOptions.map((o) => (
                            <SelectItem key={o.id} value={o.id}><span className={cn(o.color && colorVariantClasses[o.color]?.text)}>{o.label}</span></SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : entity.flagType == null || entity.flagType === FlagType.MigrationEntityBlockedByUser ? (
                      <Button
                        className={cn("w-full max-w-[150px] justify-start text-left", entity.flagType === FlagType.MigrationEntityBlockedByUser ? colorVariantClasses.lime.text : colorVariantClasses.warning.text)}
                        variant="secondary" aria-label="Block/Unblock"
                        disabled={isDisabled || userRole === UserRole.Viewer || (!isAssignedUser && userRole !== UserRole.Admin)}
                        size="sm"
                        onClick={() => handleBlockModal(entityKey, entity.id, entity.flagType !== FlagType.MigrationEntityBlockedByUser, true)}>
                        {entity.flagType === FlagType.MigrationEntityBlockedByUser ? <><LockOpen className="size-4" />Unblock</> : <><Ban className="size-4" />Block</>}
                      </Button>
                    ) : <></>}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>{getActiveButton(entity, isDisabled)}</TableCell>
                </TableRow>
              );
            })}
            {entities.length === 0 && !isFetching && (
              <TableRow className="hover:bg-transparent"><TableCell colSpan={99}>
                <TableEmptyState title={config.emptyMessage} />
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination page={page} rowsPerPage={rowsPerPage} rows={rows}
          onPageChange={setPage} onRowsPerPageChange={(val) => { setRowsPerPage(val); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]} />
      </div>

      {dirtyFlagStatus && config.renderFlagModal({
        open: isFlagModalOpen,
        entityKey: dirtyEntityKey,
        flagStatus: dirtyFlagStatus,
        onClose: () => handleFlagStatusModal("", "", MigrationFlagStatus.UnResolved, false),
        onConfirm: async () => {
          try {
            await config.updateFlag(dirtyEntityId, dirtyFlagStatus);
            await invalidateAfterMutation(dirtyEntityId);
            handleFlagStatusModal("", "", MigrationFlagStatus.UnResolved, false);
            toast.success("Flag updated");
          } catch (error: any) {
            toast.error(error?.message ?? "Failed to update flag");
          }
        },
      })}

      <ConfirmMigrationEntityBlockUpdateModal
        open={isBlockModalOpen} isBlocked={isBlocked} entityKey={dirtyEntityKey}
        config={config.blockConfig}
        onClose={() => handleBlockModal("", "", false, false)}
        onConfirm={async () => {
          try {
            await config.updateBlock(dirtyEntityId, isBlocked);
            await invalidateAfterMutation(dirtyEntityId);
            handleBlockModal("", "", false, false);
            toast.success(isBlocked ? "Blocked" : "Unblocked");
          } catch (error: any) {
            toast.error(error?.message ?? `Failed to ${isBlocked ? "block" : "unblock"}`);
          }
        }}
      />
    </div>
  );
}

export default MigrationEntityTable;
